/**
 * TechCraft CZ/SK - frontend entry point.
 *
 * Every page is rendered on the server, the frontend only enhances it with
 * the interactive bits: the mobile menu, the notification bell, the chat,
 * the accordions, the filters, the BBCode editor, the forum reactions and the
 * live search.
 */

import { initAccordions } from "./components/accordion";
import { initBackToTop } from "./components/backToTop";
import { initBanDialog, initBanEditor } from "./components/banDialog";
import { initCaptchaForms } from "./components/captcha";
import { initChat } from "./components/chat";
import { initChartHover } from "./components/chartHover";
import { initChipPickers } from "./components/chipPicker";
import { initPollDialogs } from "./components/pollDialog";
import { initClipboard } from "./components/clipboard";
import { initFeedOffers } from "./components/feedOffer";
import { initGeoProgress } from "./components/geoProgress";
import { initRuleDialog } from "./components/ruleDialog";
import { initMailComposer } from "./components/mailComposer";
import { initLetters } from "./components/letters";
import { initProofOfWork } from "./components/proofOfWork";
import { initConfirmations, initInvalidReveal, initSubmitGuards } from "./components/confirm";
import { initCounters } from "./components/counter";
import { initEditors } from "./components/editor";
import { initInstantToggles } from "./components/instantToggle";
import { initLiveSearch } from "./components/liveSearch";
import { initMentionPickers } from "./components/mentionPicker";
import { initElementFilters, initTableFilters } from "./components/filter";
import { initGolds } from "./components/gold";
import { initNavigation } from "./components/navigation";
import { initNotifications } from "./components/notifications";
import { initOwnHistory } from "./components/ownHistory";
import { initPasswordFields } from "./components/password";
import { initReactions } from "./components/reactions";
import { initQuestBoards } from "./components/questBoard";
import { initReady } from "./components/ready";
import { initRecipients } from "./components/recipients";
import { initReveals } from "./components/reveal";
import { initSortables } from "./components/sortable";
import { initTabs } from "./components/tabs";
import { initGroupProgress, initTranslationProgress } from "./components/translations";
import { initScrollToEnd } from "./components/scrollEnd";
import { initSelects } from "./components/select";
import { initSkinRename } from "./components/skinRename";
import { initSkinViewers } from "./components/skinViewer";
import { initTicketReply } from "./components/ticketReply";
import { initUnsavedGuards } from "./components/unsaved";
import { initUploads } from "./components/upload";
import { initUserMenu } from "./components/userMenu";
import { initToasts } from "./core/toast";
import { initFeed } from "./sections/feed";
import { initStatistics } from "./sections/statistics";

/** Wire every enhancement of the current page. */
export function bootstrap(): void {
    initNavigation();
    initUserMenu();
    initNotifications();
    initChat();
    initToasts();
    initAccordions();
    initClipboard();
    initFeedOffers();
    initProofOfWork();
    initGeoProgress();
    initRuleDialog();
    initCounters();
    initTableFilters();
    initElementFilters();
    initPasswordFields();
    // Before the reveals, which switch selects off: the list of one has to
    // exist by the time it is told that it is out of play.
    initChartHover();
    initChipPickers();
    initPollDialogs();
    initSelects();
    initConfirmations();
    initSubmitGuards();
    initInvalidReveal();
    initEditors();
    initMentionPickers();
    initOwnHistory();
    initInstantToggles();
    initGolds();
    initLiveSearch();
    initReactions();
    initQuestBoards();
    initReady();
    initRecipients();
    initReveals();
    initMailComposer();
    initLetters();
    initSkinRename();
    initSkinViewers();
    initSortables();
    initTabs();
    initTranslationProgress();
    initGroupProgress();
    initUnsavedGuards();
    initUploads();
    initBackToTop();
    initBanDialog();
    initBanEditor();
    initCaptchaForms();
    initTicketReply();
    initScrollToEnd();
    initFeed();
    initStatistics();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
} else {
    bootstrap();
}
